/**
 * Scale factors are kept at two decimals, always rounded down, so a fit is never off by the last fraction.
 *
 * Floored to a hundredth of one, never to nothing: a table whose narrowest width is more than a hundred
 * pages wide — one cell holding an unbroken token, a base64 blob or a JWT — would otherwise round to `zoom: 0`,
 * which browsers reject and compute back to `1`. The table would then print at full size and be cut off,
 * which is the opposite of what asking for a smaller scale meant.
 */
const shrink = (factor: number, ratio: number): number => Math.max(0.01, Math.floor(factor * ratio * 100) / 100);

/** Set once a table has been fitted, so a second pass over the same subtree leaves it alone. */
const FITTED = "printFitted";

/**
 * A scaled table is laid out in its own, wider coordinate space, so the first factor is a prediction: column
 * widths land on different sub-pixel boundaries there. A second round measures what actually happened and
 * takes off whatever is still sticking out.
 */
const ROUNDS = 2;

/** The table this node stands for: `data-component="table"` sits on a wrapper in one renderer, on the table itself in the other. */
const tableOf = (node: HTMLElement): HTMLTableElement | null =>
	node instanceof HTMLTableElement ? node : node.querySelector<HTMLTableElement>(":scope > table");

/** Only this table's own cells and columns — a table nested in a cell keeps its own layout and is fitted separately. */
const ownCells = (table: HTMLTableElement): HTMLElement[] => [
	// The last selector is for a row appended straight to the table. The HTML parser always inserts `tbody`,
	// so nothing the renderers build looks like that — but a cell missed here silently keeps
	// `word-break: break-word`, and a table measured with that on always answers "it fits".
	...table.querySelectorAll<HTMLElement>(
		":scope > thead > tr > *, :scope > tbody > tr > *, :scope > tfoot > tr > *, :scope > tr > *",
	),
];
const ownCols = (table: HTMLTableElement): HTMLElement[] => [
	...table.querySelectorAll<HTMLElement>(":scope > colgroup > col"),
];

/**
 * Scales a table that cannot be wrapped into the page down until it fits.
 *
 * `PrintColGroup` hands every column a share of `usablePageWidth`, so a printed table is always exactly
 * page-wide — nothing is cut, and nothing is scaled. For eighteen columns that share is about fifty pixels,
 * and `word-break: break-word` on the cells is the legacy alias of `overflow-wrap: anywhere`: it collapses
 * the table's intrinsic width to one character per column, so the table "fits" while a header like `rule_id`
 * prints as four stacked glyphs.
 *
 * The rule is whether wrapping can still help. `min-content` measured with `word-break: normal` and
 * `overflow-wrap: normal` is the narrowest the table can be while every value keeps its own breaks — the
 * longest token of each column. A table that fits there is left exactly as it was: an ordinary three-column
 * table of prose wraps, which is what a reader wants, and it never reaches the scaling below.
 *
 * Overflow is not the measure. A page-wide table never overflows anything — that is what the colgroup
 * guarantees — so an overflow test answers "no need to scale" for every article table there is.
 *
 * A scaled table keeps the `min-content` proportions rather than the author's column widths. Not a
 * shortcut: with the author's percentages restored, the narrowest column still has to hold its longest
 * token, which forces the whole table smaller than the sum of the columns' own minima would. Content
 * proportions are the layout that needs the least zoom, and the least zoom is the readable one.
 *
 * `zoom` and not `transform: scale`, because zoom reflows: the paginator measures these blocks right after
 * this pass and has to see the height the page will really have.
 *
 * Returns whether the table was actually scaled — a table left alone needs no re-measuring.
 */
export const fitPrintTable = (node: HTMLElement): boolean => {
	if (node.dataset[FITTED]) return false;

	const table = tableOf(node);
	if (!table) return false;

	const port = table.parentElement;
	if (!port) return false;

	// Both ends in page pixels. `clientWidth` is reported in the local space of whatever zoom is above it,
	// and mixing the two silently compares a scaled number against an unscaled one.
	const available = port.getBoundingClientRect().width;
	if (!available) return false;

	const cells = ownCells(table);
	const cols = ownCols(table);
	const before = {
		width: table.style.width,
		maxWidth: table.style.maxWidth,
		cells: cells.map((cell) => [cell.style.wordBreak, cell.style.overflowWrap] as const),
		cols: cols.map((col) => [col.style.width, col.style.minWidth] as const),
	};

	for (const cell of cells) {
		cell.style.wordBreak = "normal";
		cell.style.overflowWrap = "normal";
	}
	for (const col of cols) {
		col.style.width = "auto";
		col.style.minWidth = "0";
	}
	table.style.width = "min-content";
	table.style.maxWidth = "none";

	const minContent = table.getBoundingClientRect().width;

	if (minContent <= available) {
		// Wrapping is enough. Put back the layout the colgroup asked for and leave no trace.
		cells.forEach((cell, index) => {
			[cell.style.wordBreak, cell.style.overflowWrap] = before.cells[index];
		});
		cols.forEach((col, index) => {
			[col.style.width, col.style.minWidth] = before.cols[index];
		});
		table.style.width = before.width;
		table.style.maxWidth = before.maxWidth;
		node.dataset[FITTED] = "1";
		return false;
	}

	// The emergency break stays available once the table is at its narrowest: it cannot make the table any
	// narrower than `min-content`, and it keeps a single freakishly long token from running out of its cell.
	for (const cell of cells) cell.style.overflowWrap = "break-word";

	let factor = 1;
	for (let round = 0; round < ROUNDS; round++) {
		const width = table.getBoundingClientRect().width;
		if (width <= available) break;
		factor = shrink(factor, available / width);
		table.style.setProperty("zoom", String(factor));
	}

	// The layout is pinned in pixels, not left as `min-content`.
	//
	// A table taller than a page is split into one fragment per page, and the splitter clones this shell and
	// this colgroup for each of them. A shell that says `min-content` makes every fragment work its own width
	// out again, from only the rows it happens to hold — so the same table comes out a different width, with
	// different columns, on every page. Pinning what was measured here gives all of them the one layout.
	//
	// It also takes back what flooring the factor to a hundredth costs: the table is asked for exactly the
	// width the page has, instead of whatever the rounded-down scale happens to leave.
	//
	// Widths are read off the first row and divided by the factor: `getBoundingClientRect` is in page pixels,
	// and what is written back is the table's own space. A column the first row spans with `colspan` is not
	// pinned at all — there is no honest per-column width to read for it, and leaving it to the browser is
	// better than inventing one.
	const firstRowCells = [...(table.rows[0]?.cells ?? [])];
	const spansNothing = firstRowCells.every((cell) => cell.colSpan === 1);
	if (spansNothing && firstRowCells.length === cols.length) {
		const widths = firstRowCells.map((cell) => cell.getBoundingClientRect().width / factor);
		cols.forEach((col, index) => {
			const width = widths[index];
			if (width !== undefined) col.style.width = `${width}px`;
		});
	}
	table.style.width = `${available / factor}px`;
	table.style.maxWidth = "none";

	node.dataset[FITTED] = "1";
	return true;
};

export default fitPrintTable;
